"use client";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { imageThumbnailUrl } from "@/lib/imageUrls";

/**
 * Shown when the merchant's subscription is suspended/cancelled/expired so we
 * can pre-empt the menu render with a clear "unavailable" message instead of
 * an empty/broken menu. Mirrors the v1 Angular implementation at
 * `online-ordering/src/app/pages/merchant-unavailable/merchant-unavailable.component.ts`.
 *
 * Backend order endpoints are also gated (subscription production-readiness
 * Phase 0.2); this component is purely the UX layer.
 */
export function MerchantUnavailable({
  restaurantName,
  logoUrl,
}: {
  restaurantName?: string | null;
  logoUrl?: string | null;
}) {
  const { t } = useTranslation();
  const title = t("v2.merchant_unavailable.title");
  const body = t("v2.merchant_unavailable.body");
  const [logoFailed, setLogoFailed] = useState(false);
  const logoThumb = useMemo(() => imageThumbnailUrl(logoUrl, "400x400"), [logoUrl]);
  const logoSrc = logoFailed || !logoThumb ? logoUrl : logoThumb;

  return (
    <div
      role="alert"
      aria-live="polite"
      className="flex min-h-full flex-col items-center justify-center px-6 py-16 text-center"
    >
      <div className="flex h-20 w-20 items-center justify-center rounded-full bg-gray-100">
        {logoSrc ? (
          <img
            src={logoSrc}
            alt={restaurantName ?? ""}
            className="h-16 w-16 rounded-full object-cover"
            onError={() => {
              if (!logoFailed) setLogoFailed(true);
            }}
          />
        ) : (
          <svg
            width="36"
            height="36"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-gray-400"
            aria-hidden="true"
          >
            <path d="M3 9l1-4h16l1 4" />
            <path d="M4 9v11h16V9" />
            <path d="M9 22V12h6v10" />
          </svg>
        )}
      </div>
      <h1 className="mt-6 text-2xl font-semibold text-gray-900">{title}</h1>
      {restaurantName ? (
        <p className="mt-1 text-sm font-medium text-gray-500">
          {restaurantName}
        </p>
      ) : null}
      <p className="mt-3 max-w-md text-base leading-relaxed text-gray-600">
        {body}
      </p>
    </div>
  );
}

// Subscription statuses that indicate the merchant should not be shown to
// public ordering customers. Mirrors the v1 guard `BLOCKED_STATUSES` set in
// `online-ordering/src/app/guards/merchant-status.guard.ts`.
const BLOCKED_STATUSES = new Set<string>([
  "suspended",
  "cancelled",
  "canceled",
  "expired",
]);

/**
 * Returns true when the given subscription status indicates the storefront
 * should be hidden from public customers. Empty / unknown values are treated
 * as "open" — the backend remains the source of truth and already gates the
 * actual order/payment endpoints.
 */
export function isMerchantBlocked(
  subscriptionStatus: string | null | undefined,
): boolean {
  if (!subscriptionStatus) return false;
  return BLOCKED_STATUSES.has(subscriptionStatus.toLowerCase().trim());
}
